import React from "react";
import styled from "styled-components";

import { useQueryGetCourseUnits } from "@examspark/common-app";
import { Label, RouterLink, Tooltip, VSpace } from "@examspark/common-ui";
import { IconSuccessSolid } from "@examspark/common-ui/dist/components/icons";
import { LabelText } from "@examspark/common-ui/dist/components/inputs/Label";
import { colorFromTheme } from "@examspark/common-ui/dist/utils/color";
import { Classroom, Maybe } from "@examspark/types";

import UnitStatusBadge from "../classroom/units/UnitStatusBadge";

const CourseProgressContainer = styled.div`
  display: flex;
  flex-direction: column;

  background-color: #f6f6f6;

  box-shadow: 0 0 0 1px ${colorFromTheme("surface5")};
  border-radius: 0.5rem;
  overflow: hidden;
`;

const CourseProgressPager = styled.nav`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  background-color: #f0f0f0;
  box-shadow: 0 0 0 1px ${colorFromTheme("surface5")};
`;

const CourseProgressPreviousNext = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 1rem;
  gap: 2rem;
  flex: 1;

  & > svg {
    flex-shrink: 0;
    fill: #555;
  }

  // make the NEXT text align right
  &:nth-child(2) {
    text-align: right;

    ${LabelText} {
      align-self: flex-end;
    }
  }

  max-width: fit-content;
`;

const CourseProgressBarItemBar = styled.div<{
  status: Maybe<string> | undefined;
}>`
  height: 1.2rem;
  width: 100%;
  background-color: ${(props) => {
    switch (props.status) {
      case "COMPLETE":
        return "#a475d3";
      case "SKIPPED":
        return "#666";
      case "COMPLETE_GOOD":
        return "#2aa82a";
      case "COMPLETE_FAIR":
        return "#e6b252";
      case "COMPLETE_POOR":
        return "#ef5f5f";
      case "IN_PROGRESS":
        return "rebeccapurple";
      default:
        return colorFromTheme("surface5");
    }
  }};
`;

const CourseProgressBarItemLabel = styled.div`
  font-size: 1.1rem;
  color: #777;
`;

const CourseProgressBarItem = styled.div<{ size: number }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-basis: ${(props) => props.size}%;
  flex-grow: 1;
  gap: 0.4rem;
`;

const CourseProgressBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 2rem 1.5rem;

  gap: 1px;

  & > ${CourseProgressBarItem}:first-child ${CourseProgressBarItemBar} {
    border-radius: 0.5ch 0 0 0.5ch;
  }

  @media (max-width: 600px) {
    & > ${CourseProgressBarItem}:nth-child(2n) ${CourseProgressBarItemLabel} {
      visibility: hidden;
    }
  }
`;

type CourseProgressProps = {
  currentUnitId: string | undefined | null;
  classroom: Classroom;
  scoresByUnit?: Record<string, number>;
};
const CourseProgress = (props: CourseProgressProps) => {
  const { data: courseUnits } = useQueryGetCourseUnits(
    props.classroom.courseId,
  );
  const classroomUnits = props.classroom.units;
  const currentUnitIndex = courseUnits?.findIndex(
    (unit) => unit.id === props.currentUnitId,
  );
  const previousUnit = courseUnits?.[(currentUnitIndex ?? 0) - 1];
  const nextUnit = courseUnits?.[(currentUnitIndex ?? 0) + 1];

  // hide course progress if there are no units
  // in the selected course
  if (courseUnits?.length === 0) {
    return null;
  }

  return (
    <CourseProgressContainer>
      <CourseProgressBar>
        {courseUnits?.map((unit, index) => {
          const classroomUnit = classroomUnits?.find(
            (cu) => cu.unit?.id === unit.id,
          );

          let status = classroomUnit?.maturity;
          const score = props.scoresByUnit?.[unit.id];
          console.log("score", score);
          if (status === "COMPLETE" && score !== undefined) {
            if ((score ?? 0) > 0.8) {
              status = "COMPLETE_GOOD";
            } else if ((score ?? 0) > 0.6) {
              status = "COMPLETE_FAIR";
            } else {
              status = "COMPLETE_POOR";
            }
          }

          const title = unit.name ?? "";
          return (
            <CourseProgressBarItem size={0} title={title} key={unit.id}>
              <CourseProgressBarItemLabel>
                {unit.numberLabel ?? (index + 1).toString()}
              </CourseProgressBarItemLabel>
              <Tooltip
                tip={
                  <VSpace
                    style={{ width: "20rem", padding: "0.5rem" }}
                    size="0.8rem"
                  >
                    <div>
                      <div style={{ display: "flex" }}>
                        <UnitStatusBadge classroomUnit={classroomUnit} />
                      </div>
                    </div>
                    <div style={{ textAlign: "left" }}>{title}</div>
                  </VSpace>
                }
                placement="top"
              >
                <CourseProgressBarItemBar status={status} />
              </Tooltip>
            </CourseProgressBarItem>
          );
        })}
        <IconSuccessSolid
          fill={"rebeccapurple"}
          style={{ position: "relative", bottom: "-9px", left: "-4px" }}
          width="24px"
        />
      </CourseProgressBar>
      <CourseProgressPager>
        {previousUnit != null ? (
          <CourseProgressPreviousNext>
            <Label label="Previous">
              <RouterLink to={`unit/${previousUnit.id}`}>
                {previousUnit.name}
              </RouterLink>
            </Label>
          </CourseProgressPreviousNext>
        ) : (
          // empty so that the next element is aligned to the right
          <div></div>
        )}
        {nextUnit != null ? (
          <CourseProgressPreviousNext>
            <Label label="Next">
              <RouterLink to={`unit/${nextUnit.id}`}>
                {nextUnit.name}
              </RouterLink>
            </Label>
          </CourseProgressPreviousNext>
        ) : (
          // empty so that the previous element is aligned to the left
          <div></div>
        )}
      </CourseProgressPager>
    </CourseProgressContainer>
  );
};

export default CourseProgress;
